import React, { useEffect, useState } from "react";
import { Download, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, downloadFile } from "../lib/api";

const STATUS_STYLE = {
  Current: { borderColor: "var(--border-strong)", color: "var(--ink)" },
  "Due soon": { borderColor: "#FFC72C", color: "#8A6A00" },
  Expired: { borderColor: "#FF9E1B", color: "#9B2C1C" },
  "Never accepted": { borderColor: "#FF9E1B", color: "#9B2C1C" },
};

/** Approver attestation status. `compact` renders the table only, for embedding on the Audit page. */
export function AttestationReport({ compact = false }) {
  const [report, setReport] = useState(null);

  useEffect(() => {
    api.get("/admin/attestations")
      .then(({ data }) => setReport(data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, []);

  const exportCsv = async () => {
    try {
      await downloadFile("/admin/attestations/export", "attestation-report.csv");
      toast.success("Attestation report downloaded");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const rows = report?.rows || [];

  return (
    <div data-testid="attestation-report">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
        <div>
          <div className="eyebrow flex items-center gap-2"><ShieldCheck size={13}/> Approver attestations</div>
          {!compact && (
            <h2 className="font-heading font-black text-3xl mt-1 leading-none tracking-tight" style={{ color: "var(--ink)" }}>
              Who has accepted
            </h2>
          )}
          <p className="mt-2 text-sm" style={{ color: "var(--muted)" }} data-testid="attestation-summary">
            {report
              ? `${report.approvers} approver(s) · ${report.outstanding} outstanding · current version ${report.current_version}`
              : "Loading…"}
          </p>
        </div>
        <button onClick={exportCsv} className="btn-ghost flex items-center gap-2" data-testid="attestation-export">
          <Download size={14}/> Export CSV
        </button>
      </div>

      <div className="border overflow-x-auto" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
        <table className="w-full text-sm min-w-[820px]" data-testid="attestation-table">
          <thead>
            <tr className="border-b eyebrow text-left" style={{ borderColor: "var(--border)" }}>
              <th className="px-4 py-3">Employer</th>
              <th className="px-4 py-3">Approver</th>
              <th className="px-4 py-3">Accepted</th>
              <th className="px-4 py-3">Expires</th>
              <th className="px-4 py-3">Version</th>
              <th className="px-4 py-3 text-right">Acceptances</th>
              <th className="px-4 py-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} className="border-b" style={{ borderColor: "var(--border)" }} data-testid={`attestation-row-${row.username}`}>
                <td className="px-4 py-3 font-medium" style={{ color: "var(--ink)" }}>{row.company || "—"}</td>
                <td className="px-4 py-3" style={{ color: "var(--muted)" }}>
                  {row.name}
                  <span className="block text-xs">{row.work_email}</span>
                </td>
                <td className="px-4 py-3" style={{ color: "var(--ink)" }}>{row.accepted_at || "—"}</td>
                <td className="px-4 py-3" style={{ color: "var(--ink)" }}>{row.expires_at || "—"}</td>
                <td className="px-4 py-3 font-mono text-xs" style={{ color: "var(--muted)" }}>{row.version || "—"}</td>
                <td className="px-4 py-3 text-right" style={{ color: "var(--muted)" }}>{row.acceptances}</td>
                <td className="px-4 py-3">
                  <span className="pill text-[10px]" style={STATUS_STYLE[row.status]} data-testid={`attestation-status-${row.username}`}>
                    {row.status}
                  </span>
                </td>
              </tr>
            ))}
            {report && !rows.length && (
              <tr><td colSpan={7} className="p-10 text-center text-sm" style={{ color: "var(--muted)" }} data-testid="attestation-empty">
                No approvers have been designated yet.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function AdminAttestations() {
  return (
    <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-10" data-testid="admin-attestations-page">
      <div className="mb-8">
        <div className="eyebrow mb-2">Administration</div>
        <h1 className="font-heading font-black text-4xl sm:text-5xl leading-none tracking-tight" style={{ color: "var(--ink)" }}>
          Attestations
        </h1>
        <p className="mt-3 text-base max-w-2xl" style={{ color: "var(--muted)" }}>
          Every designated approver must accept the attestation before they can approve invoices, and
          again every 12 months. Acceptances are written to the audit log.
        </p>
      </div>
      <AttestationReport />
    </div>
  );
}
